import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { fetchProducts } from "../lib/supabase";
import { normalizeProduct } from "../data/products";

const StoreContext = createContext(null);

const readLS = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch (err) {
    console.warn(`StoreContext: failed to read ${key} from localStorage`, err);
    return fallback;
  }
};
const writeLS = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (err) {
    console.warn(`StoreContext: failed to write ${key} to localStorage`, err);
  }
};

export const INR_TO_USD = 1 / 85;

export const formatPrice = (priceInr, currency) => {
  if (currency === "USD") {
    const usd = Math.round(priceInr * INR_TO_USD);
    return `$${usd.toLocaleString("en-US")}`;
  }
  return `\u20B9${Math.round(priceInr).toLocaleString("en-IN")}`;
};

export const StoreProvider = ({ children }) => {
  const [cart, setCart] = useState(() => readLS("dc_cart", []));
  const [wishlist, setWishlist] = useState(() => readLS("dc_wishlist", []));
  const [currency, setCurrency] = useState(() => readLS("dc_currency", "INR"));
  const [menuOpen, setMenuOpen] = useState(false);
  const [quickBuy, setQuickBuy] = useState(null);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);

  // Load products from Supabase on mount.
  useEffect(() => {
    let cancelled = false;
    fetchProducts().then((rows) => {
      if (cancelled) return;
      setProducts(rows.map(normalizeProduct).filter(Boolean));
      setProductsLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => writeLS("dc_cart", cart), [cart]);
  useEffect(() => writeLS("dc_wishlist", wishlist), [wishlist]);
  useEffect(() => writeLS("dc_currency", currency), [currency]);

  const addToCart = useCallback((product, size, quantity = 1) => {
    setCart((prev) => {
      const idx = prev.findIndex((l) => l.id === product.id && l.size === size);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + quantity };
        return next;
      }
      return [
        ...prev,
        {
          id: product.id,
          size,
          quantity,
          name: product.name,
          priceInr: product.priceInr,
          image: product.images?.[0],
          category: product.category,
        },
      ];
    });
  }, []);

  const updateQty = useCallback((id, size, qty) => {
    setCart((prev) =>
      prev.map((l) => (l.id === id && l.size === size ? { ...l, quantity: Math.max(1, qty) } : l))
    );
  }, []);

  const removeLine = useCallback((id, size) => {
    setCart((prev) => prev.filter((l) => !(l.id === id && l.size === size)));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  /** Replace the entire cart with the given lines. Used by the abandoned-cart
   *  recovery flow (/recover/:token) so a single click restores everything. */
  const replaceCart = useCallback((lines) => {
    if (!Array.isArray(lines)) return;
    setCart(
      lines.map((l) => ({
        id: l.id,
        size: l.size,
        quantity: Math.max(1, Number(l.quantity) || 1),
        name: l.name,
        priceInr: Number(l.priceInr) || 0,
        image: l.image,
        category: l.category,
      }))
    );
  }, []);

  const toggleWishlist = useCallback((productId) => {
    setWishlist((prev) =>
      prev.includes(productId) ? prev.filter((p) => p !== productId) : [...prev, productId]
    );
  }, []);

  const cartCount = useMemo(() => cart.reduce((sum, l) => sum + l.quantity, 0), [cart]);
  const cartSubtotalInr = useMemo(
    () => cart.reduce((sum, l) => sum + l.priceInr * l.quantity, 0),
    [cart]
  );

  const findProductById = useCallback(
    (id) => products.find((p) => p.id === id) || null,
    [products]
  );

  const value = {
    cart,
    cartCount,
    cartSubtotalInr,
    wishlist,
    currency,
    setCurrency,
    addToCart,
    updateQty,
    removeLine,
    clearCart,
    replaceCart,
    toggleWishlist,
    menuOpen,
    setMenuOpen,
    quickBuy,
    setQuickBuy,
    products,
    productsLoading,
    findProductById,
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
};

export const useStore = () => {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error("useStore must be used inside StoreProvider");
  return ctx;
};
